<template>
  <div :class="{ 'service-visits-board-page--fullscreen p-4': fullscreen }">
    <b-card no-body class="card-custom service-visits-board" header-tag="header">
      <template v-slot:header>
        <div class="card-title service-visits-board__title">
          <h3 class="card-label">
            {{ $t("MENU.ITEM.SERVICE.VISITS") }}
            <i class="mr-2"></i>
            <small>{{ $t("SERVICE_VISITS.BOARD") }}</small>
          </h3>
        </div>
        <div class="card-toolbar service-visits-board__toolbar">
          <router-link
            v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{
              name: fullscreen
                ? 'fullscreen-list-service-visits'
                : 'list-service-visits'
            }"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-board__toolbar-button"
          >
            <i class="flaticon2-list"></i>
            {{ $t("LABELS.LIST") }}
          </router-link>
          <router-link
            v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{
              name: fullscreen
                ? 'fullscreen-calendar-service-visits'
                : 'calendar-service-visits'
            }"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-board__toolbar-button"
          >
            <i class="flaticon-calendar"></i>
            {{ $t("SERVICE_VISITS.CALENDAR") }}
          </router-link>
          <router-link
            v-if="!fullscreen && checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
            :to="{ name: 'fullscreen-board-service-visits' }"
            target="_blank"
            class="btn btn-light-primary font-weight-bolder ml-2 service-visits-board__toolbar-button service-visits-board__fullscreen-link"
          >
            <i class="flaticon2-expand"></i>
            {{ $t("BUTTON.FULLSCREEN") }}
          </router-link>
          <router-link
            v-permission="['service-visits-create']"
            :to="{ name: 'add-service-visits' }"
            class="btn btn-light-success font-weight-bolder ml-2 service-visits-board__toolbar-button"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}
          </router-link>
        </div>
      </template>

      <v-skeleton-loader v-if="isPageLoading" type="article" />

      <div v-else class="p-5">
        <ServiceVisitFilters
          :filters.sync="filters"
          :mechanics="mechanics"
          :priorities="priorities"
          :service-locations="serviceLocations"
          :show-mechanic="canShowServiceVisitMechanicFilter"
          :show-status="false"
          :hide-search-input="true"
          mobile-sheet
          @search="applyServiceVisitFilters"
          @clear="clearServiceVisitFilters"
        />

        <div
          ref="boardScroll"
          class="board-scroll row flex-nowrap flex-lg-wrap mx-0"
          :class="{ 'board-scroll--page-scroll': fullscreen }"
        >
          <ServiceVisitBoardColumn
            v-for="column in columns"
            :key="column.status.id"
            :column="column"
            :item-statuses="itemStatuses"
            :is-moving-visit="isMovingVisit"
            :can-move-visit="canMoveVisit"
            :is-dragged-visit="isDraggedVisit"
            :is-visit-drop-zone="isVisitDropZone"
            :fullscreen="fullscreen"
            @visit-pointerdown="handleVisitPointerDown"
            @task-updated="updateTaskItem"
            @task-created="addTaskItem"
            @tasks-reordered="replaceTaskItems"
          />
        </div>
      </div>
    </b-card>
    <div
      v-if="dragPreview"
      class="service-visit-board-drag-preview position-fixed"
      :style="dragPreviewStyle"
    >
      <ServiceVisitBoardCard
        :visit="dragPreview.visit"
        :item-statuses="itemStatuses"
      />
    </div>
    <ServiceVisitCompletionModal
      ref="completionModal"
      modal-id="finish-service-visit-modal"
      :visit="finishVisit"
      :saving="isMovingVisit"
      @complete="completeServiceVisit"
      @hidden="resetFinishVisitModal"
    />
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import { serviceVisitBoardDatasetMixin } from "@/modules/service-visits/mixins/serviceVisitBoardDatasetMixin.js";
import { serviceVisitBoardDragMixin } from "@/modules/service-visits/mixins/serviceVisitBoardDragMixin.js";
import { serviceVisitBoardTasksMixin } from "@/modules/service-visits/mixins/serviceVisitBoardTasksMixin.js";
import { serviceVisitFilterOptionsMixin } from "@/modules/service-visits/mixins/serviceVisitFilterOptionsMixin.js";
import ServiceVisitBoardCard from "@/modules/service-visits/components/ServiceVisitBoardCard.vue";
import ServiceVisitBoardColumn from "@/modules/service-visits/components/ServiceVisitBoardColumn.vue";
import ServiceVisitCompletionModal from "@/modules/service-visits/components/ServiceVisitCompletionModal.vue";
import ServiceVisitFilters from "@/modules/service-visits/components/ServiceVisitFilters.vue";

export default {
  name: "ServiceVisitsBoard",
  props: {
    fullscreen: {
      type: Boolean,
      default: false
    }
  },
  mixins: [
    permissionMixin,
    serviceVisitBoardDatasetMixin,
    serviceVisitBoardDragMixin,
    serviceVisitBoardTasksMixin,
    serviceVisitFilterOptionsMixin
  ],
  components: {
    ServiceVisitBoardCard,
    ServiceVisitBoardColumn,
    ServiceVisitCompletionModal,
    ServiceVisitFilters
  },
  data() {
    return {
      finishVisitPayload: null,
      finishVisit: null
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.SERVICE.VISITS"),
        route: { name: "board-service-visits" }
      },
      { title: this.$i18n.t("SERVICE_VISITS.BOARD") }
    ]);
    this.loadServiceVisitFilterOptions({ includeStatuses: false });
    this.getBoard();
  },
  methods: {
    applyServiceVisitFilters() {
      this.getBoard();
    },
    clearServiceVisitFilters() {
      this.resetServiceVisitFilters();
      this.getBoard();
    },
    handleVisitPointerDown({ visit, statusId, event }) {
      this.startVisitPointerDrag(visit, statusId, event);
    },
    openFinishVisitModal(payload, visit) {
      this.finishVisitPayload = payload;
      this.finishVisit = visit;
      this.$nextTick(() => this.$refs.completionModal.open());
    },
    completeServiceVisit(completionDetails) {
      if (!this.finishVisitPayload) {
        return;
      }

      this.moveVisit({
        ...this.finishVisitPayload,
        ...completionDetails
      })
        .then(() => this.$refs.completionModal.close())
        .catch(response => {
          this.showVisitReorderFailedToast(response);
        });
    },
    resetFinishVisitModal() {
      this.finishVisitPayload = null;
      this.finishVisit = null;
    }
  }
};
</script>

<style scoped>
.service-visits-board ::v-deep .card-body {
  padding: 0;
}

.service-visits-board-page--fullscreen {
  min-width: calc(100vw - 2rem);
  width: max-content;
}

.service-visits-board-page--fullscreen ::v-deep .card {
  min-width: calc(100vw - 2rem);
  overflow: visible;
  width: max-content;
}

.service-visits-board-page--fullscreen ::v-deep .card-body {
  min-width: calc(100vw - 2rem);
  overflow: visible;
  width: max-content;
}

.board-scroll {
  min-height: 560px;
  overflow-x: auto;
}

.board-scroll--page-scroll {
  flex-wrap: nowrap !important;
  min-width: max-content;
  overflow: visible;
  width: max-content;
}

.service-visit-board-drag-preview {
  left: 0;
  pointer-events: none;
  top: 0;
  z-index: 2000;
}

.service-visit-board-drag-preview ::v-deep article {
  margin-bottom: 0 !important;
}

@media (max-width: 767.98px) {
  .service-visits-board ::v-deep .card-header {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .service-visits-board__title {
    flex: 1 1 100%;
    margin-bottom: 0.75rem;
  }

  .service-visits-board__toolbar {
    display: grid;
    flex: 1 1 100%;
    gap: 0.5rem;
    grid-auto-columns: minmax(0, 1fr);
    grid-auto-flow: column;
  }

  .service-visits-board__toolbar-button {
    align-items: center;
    display: flex;
    font-size: 0.75rem;
    justify-content: center;
    margin-left: 0 !important;
    min-height: 2.5rem;
    padding: 0.5rem;
    white-space: nowrap;
  }

  .service-visits-board__toolbar-button i {
    margin-right: 0.25rem;
  }

  .service-visits-board__fullscreen-link {
    display: none;
  }
}
</style>
